import { Button } from "@ui/Button";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { ProgressBar } from "@ui/ProgressBar";
import { HStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import {
	createEffect,
	createResource,
	createSignal,
	For,
	Match,
	onCleanup,
	onMount,
	Show,
	Switch,
} from "solid-js";
import {
	getChatCosts,
	type QuestionChatCostCategory,
	type QuestionChatCostsResponse,
} from "@/client";
import {
	QUESTION_ANSWER_COMPLETE_EVENT,
	type QuestionAnswerCompleteEventDetail,
} from "~/modules/shared/chat/constants";
import { ChatTimeWorkedStat } from "./ChatTimeWorkedStat";
import { ChatUsageStat } from "./ChatUsageStat";
import {
	formatCredits,
	formatRefreshedAt,
	getCategoryBarOffsets,
	isIncludedCategory,
	REFRESHED_AT_TICK_MS,
} from "./formatters";

const USAGE_POLL_DELAYS_MS = [3000, 5000, 10000, 15000, 20000, 20000];

type Props = {
	questionId: string;
	onHasUsageChange?: (hasUsage: boolean) => void;
	chatThinking: boolean;
	/** Studio lists credit share by category; AskGVT shows totals and time only. */
	showCreditBreakdown?: boolean;
};

export const ChatCostPanel = (props: Props) => {
	const showCreditBreakdown = () => props.showCreditBreakdown !== false;
	const [detailsOpen, setDetailsOpen] = createSignal(true);
	const [usage, setUsage] = createSignal<
		QuestionChatCostsResponse | undefined
	>();
	const [refreshedAt, setRefreshedAt] = createSignal<number | undefined>();
	const [now, setNow] = createSignal(Date.now());

	onMount(() => {
		const tick = window.setInterval(
			() => setNow(Date.now()),
			REFRESHED_AT_TICK_MS,
		);
		onCleanup(() => window.clearInterval(tick));
	});

	const [usageData, { refetch }] = createResource(
		() => props.questionId,
		async (questionId): Promise<QuestionChatCostsResponse> => {
			const response = await getChatCosts({
				path: { question_id: questionId },
			});

			if (response.error) {
				throw new Error(
					(response.error as { message?: string }).message ??
						"Failed to load chat costs",
				);
			}

			setUsage(response.data);
			setRefreshedAt(Date.now());

			return response.data;
		},
	);

	createEffect(() => {
		if ((usage()?.reported_interaction_count ?? 0) > 0) {
			props.onHasUsageChange?.(true);
		}
	});

	const refreshStatus = () => {
		if (usageData.loading) {
			return "Refreshing...";
		}

		const refreshed = formatRefreshedAt(refreshedAt(), now());

		return usageData.error
			? `Refresh failed, showing figures from ${refreshed}`
			: `Last refreshed ${refreshed}`;
	};

	onMount(() => {
		let aborted = false;
		const pendingTimers = new Set<number>();

		const sleep = (ms: number) =>
			new Promise<void>((resolve) => {
				const timer = window.setTimeout(() => {
					pendingTimers.delete(timer);
					resolve();
				}, ms);
				pendingTimers.add(timer);
			});

		const pollForNewUsage = async (knownCount: number) => {
			for (let attempt = 0; ; attempt++) {
				if (aborted) return;

				try {
					const result = await refetch();
					if (aborted) return;
					if ((result?.reported_interaction_count ?? 0) > knownCount) return;
				} catch {
					if (aborted) return;
				}

				const delay = USAGE_POLL_DELAYS_MS[attempt];
				if (delay === undefined) return;

				await sleep(delay);
			}
		};

		const onAnswerComplete = (evt: Event) => {
			const e = evt as CustomEvent<QuestionAnswerCompleteEventDetail>;
			if (e.detail?.questionId !== props.questionId) {
				return;
			}
			void pollForNewUsage(usage()?.reported_interaction_count ?? 0);
		};

		let checkedOnLoad = false;
		createEffect(() => {
			const loaded = usage();
			if (checkedOnLoad || loaded === undefined) {
				return;
			}
			checkedOnLoad = true;

			if (
				loaded.has_conversation &&
				(loaded.reported_interaction_count ?? 0) === 0
			) {
				void pollForNewUsage(0);
			}
		});

		document.addEventListener(QUESTION_ANSWER_COMPLETE_EVENT, onAnswerComplete);

		onCleanup(() => {
			aborted = true;
			for (const timer of pendingTimers) {
				window.clearTimeout(timer);
			}
			pendingTimers.clear();
			document.removeEventListener(
				QUESTION_ANSWER_COMPLETE_EVENT,
				onAnswerComplete,
			);
		});
	});

	return (
		<div class="cost-dropdown__panel">
			<Switch>
				<Match when={usage()}>
					{(currentUsage) => {
						const totalCreditsLabel = () =>
							formatCredits(currentUsage().total_credits ?? 0);
						const hasCategories = () =>
							(currentUsage().categories?.length ?? 0) > 0;
						const categories = () => currentUsage().categories ?? [];
						const categoryBarOffsets = () =>
							getCategoryBarOffsets(categories());

						return (
							<>
								<div class="cost-dropdown__header">
									<MaterialSymbol
										symbol="monitoring"
										container={{
											background: "var(--gvt-teal-light)",
											borderRadius: "0.5rem",
											size: "1.75rem",
										}}
										color="primary"
									/>
									<Text
										as="span"
										family="mono"
										size="xsmall"
										weight="bold"
										color="fg3"
										transform="uppercase"
									>
										Chat usage
									</Text>
								</div>

								<HStack spacing="large" fullWidth>
									<ChatUsageStat
										chatThinking={props.chatThinking}
										totalCredits={currentUsage().total_credits ?? 0}
									/>
									<div class="stat-divider" />
									<ChatTimeWorkedStat
										chatThinking={props.chatThinking}
										timeWorkedSeconds={currentUsage().time_worked_seconds}
									/>
								</HStack>

								<Show when={showCreditBreakdown()}>
									<div class="cost-dropdown__details">
										<button
											type="button"
											class="cost-dropdown__details-toggle"
											aria-expanded={detailsOpen()}
											onClick={() => setDetailsOpen((isOpen) => !isOpen)}
										>
											<Text
												as="span"
												family="mono"
												size="xsmall"
												weight="bold"
												color="fg3"
												transform="uppercase"
											>
												Details
											</Text>
											<MaterialSymbol
												symbol="expand_less"
												classList={{
													"cost-dropdown__chevron": true,
													"cost-dropdown__chevron--collapsed": !detailsOpen(),
												}}
												relativeSize="smaller"
											/>
										</button>

										<Show when={detailsOpen()}>
											<Show
												when={hasCategories()}
												fallback={
													<Text
														as="span"
														size="xsmall"
														color="fg3"
														style={{
															display: "block",
															"margin-top": "0.375rem",
														}}
													>
														Usage breakdown unavailable.
													</Text>
												}
											>
												<div class="cost-dropdown__breakdown">
													<div class="cost-dropdown__breakdown-head">
														<Text
															as="span"
															family="mono"
															size="xsmall"
															weight="bold"
															color="fg4"
															transform="uppercase"
															style={{ "grid-column": "1 / 3" }}
														>
															Share of the {totalCreditsLabel()} credits
														</Text>
														<Text
															as="span"
															family="mono"
															size="xsmall"
															weight="bold"
															color="fg4"
															transform="uppercase"
															style={{ "text-align": "right" }}
														>
															%
														</Text>
														<Text
															as="span"
															family="mono"
															size="xsmall"
															weight="bold"
															color="fg4"
															transform="uppercase"
															style={{ "text-align": "right" }}
														>
															Credits
														</Text>
													</div>
													<ul class="cost-dropdown__breakdown-list">
														<For each={categories()}>
															{(category: QuestionChatCostCategory, index) => (
																<li class="cost-dropdown__breakdown-row">
																	<div class="cost-dropdown__breakdown-label">
																		<MaterialSymbol
																			symbol={category.symbol}
																			relativeSize="smaller"
																			color="fg3"
																		/>
																		<Text
																			as="span"
																			size="xsmall"
																			color="ink"
																			ellipsis
																		>
																			{category.label}
																		</Text>
																	</div>
																	<div class="cost-dropdown__breakdown-bar">
																		<ProgressBar
																			value={category.percent}
																			offset={categoryBarOffsets()[index()]}
																		/>
																	</div>
																	<Text
																		as="span"
																		size="xsmall"
																		color="fg2"
																		style={{
																			"text-align": "right",
																			"font-variant-numeric": "tabular-nums",
																		}}
																	>
																		{category.percent ?? 0}%
																	</Text>
																	<Text
																		as="span"
																		size="xsmall"
																		color="fg2"
																		style={{
																			"text-align": "right",
																			"font-variant-numeric": "tabular-nums",
																		}}
																	>
																		{isIncludedCategory(category)
																			? "Included"
																			: formatCredits(category.credits ?? 0)}
																	</Text>
																</li>
															)}
														</For>
													</ul>
												</div>
											</Show>
										</Show>
									</div>
								</Show>

								<div class="cost-dropdown__footer">
									<Text as="span" size="xsmall" color="fg4">
										{refreshStatus()}
									</Text>
									<Button
										variant="ghost"
										theme="monochrome"
										size="small"
										layout="icon"
										symbol="refresh"
										aria-label="Refresh chat usage"
										disabled={usageData.loading}
										onClick={() => {
											void Promise.resolve(refetch()).catch(() => {});
										}}
									/>
								</div>
							</>
						);
					}}
				</Match>
				<Match when={usageData.loading}>
					<Text as="span" size="xsmall" color="fg3">
						Loading usage...
					</Text>
				</Match>
				<Match when={usageData.error}>
					<Text as="span" size="xsmall" color="fg3">
						Usage unavailable.
					</Text>
				</Match>
			</Switch>
		</div>
	);
};
